import {
  BehaviorSubject,
  combineLatest,
  from,
  Observable,
  defer as observableDefer,
  from as observableFrom,
  interval as observableInterval,
  merge as observableMerge,
  of,
  ReplaySubject,
} from 'rxjs';
import {
  catchError,
  combineLatestWith,
  filter,
  first,
  map,
  mergeAll,
  mergeMap,
  switchMap,
  takeUntil,
  takeWhile,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { ofType } from '@mode/legacy/util-domain';
import { ICellExportPusherEventPayload, NotebookTypes } from '@mode/notebooks/contract';
import { AppStore } from '~/types/main.types';

import {
  DeprecatedApiService,
  NotebookCellResource,
  NotebookCellRunResource,
  NotebookCellRunResultResource,
  NotebookResource,
  NotebookVisualizationResource,
  ReportResource,
  SessionResource,
} from '@mode/legacy/data-access-model-js';
import { SocketEvents, SocketService } from '@mode/shared/data-access-socket';
import { ConfirmDialogService } from '@mode/shared/ui';
import { FeatureFlagsFacade, FeatureFlag, ProducerTypes } from '@mode/shared/contract-common';
import { getTokenFromModeLink } from '@mode/shared/util-halogen';
import { AnalyticsEvent, AnalyticsService, RunMethodType } from '@mode/shared/util-segment';
import { Actions } from '../actions';
import { ClientActionType } from '../actions/client.actions';
import * as actions from '../actions/editor-actions';
import { CellRunActions } from '../actions/notebooks/cell-run.actions';
import { CellActions } from '../actions/notebooks/cell.actions';
import { NotebookActions } from '../actions/notebooks/notebook.actions';
import { ReportRunActionType } from '../actions/report-run.actions';
import { NotebookHelpers } from '../lib/notebooks';
import { observifyAngularPromise, PollingObservable } from '../lib/observables';
import {
  accountPreferenceSelector,
  selectedReportLastSuccessfulRunTokenSelector,
  selectedReportReportRunStateSelector,
  selectedReportResourceSelector,
} from '../reducers/selectors';
import { NotebookSelectors } from '../selectors/notebook.selectors';
import { WindowMessageService } from './window-message-service';
import { ToastService } from '@mode/shared/util-toast';
import { SwitchViewActions, SwitchViewFacade } from '@mode/switch-views/contract';
import { CellExportActions } from '@mode/notebooks/contract';
import { ReportFacade } from '@mode/reports/domain';

const MIN_POLL_INTERVAL = 500;
const MAX_POLL_INTERVAL = 5000;
const POLL_FACTOR = 1.2;

//TODO: Generalize these once the backend changes
const NOTEBOOK_REL = 'python_notebook';
const CELLS_REL = 'python_cells';
const LAST_RUN_REL = 'last_python_cell_run';
const NEW_RUN_REL = 'new_python_cell_run';
const NEW_BATCH_RUN_REL = 'new_python_cell_batch_run';
const CELL_RUN_REL = 'python_cell_run';
const CELL_RUNS_REL = 'cell_runs';
const REPORT_CELL_RUNS_REL = 'python_cell_runs';
const SESSIONS_REL = 'perspective_python_sessions';
const NEW_SESSION_REL = 'new_python_session';
const LAYOUT_VIZ_REL = 'python_visualizations';
const NEW_LAYOUT_VIZ_REL = 'new_python_visualization';
const CELL_REL = 'python_cell';
const NEW_CELL_REL = 'new_python_cell';
const RESULT_REL = 'python_cell_run_results';
const CONTENT_REL = 'result';
const CONCURRENT_CELLS_TO_LOAD = 10;

const createEmpty = () => Promise.resolve();

export enum NotebookPusherEvents {
  ResultCreated = 'result_created',
  NotebookIdle = 'kernel_idle',
  BatchRunComplete = 'batch_run_complete',
  DataLoaded = 'load_complete',
  SessionTerminated = 'session_terminated',
  SessionBusy = 'session_busy',
  SessionMetrics = 'session_metrics',
  ExportDataframeCompleted = 'export_dataframe_completed',
}

export class NotebookDataService {
  static $inject = [
    '$rootScope',
    'RxStoreService',
    'actions$',
    'ModeCollection',
    'ModeResource',
    'PythonSessionModel',
    'PythonNotebookModel',
    'PythonCellModel',
    'ApiService',
    'WindowMessageService',
    '$http',
    '$timeout',
    'AnalyticsService',
    '$q',
    'ConfirmDialogService',
    'SocketService',
    'FeatureFlagsFacade',
    'ToastService',
    'SwitchViewFacade',
    'ReportFacade',
  ];

  private _hasClearedOutput$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  isRegisterImportedDatasetEnabled$: Observable<boolean>;
  isNotebookExportDatasetsEnabled$: Observable<boolean>;
  isNotebookDatasetsPublishedEnabled$: Observable<boolean>;
  channels: { [name: string]: any };
  cancellableRunTokens = new Set();
  isCancelling = false;

  hasOutput$: Observable<boolean> = combineLatest([
    this.store.select(NotebookSelectors.selectedNotebookCellsExecutionCount),
    this._hasClearedOutput$.asObservable(),
  ]).pipe(map(([executionCount, hasClearedOutput]) => executionCount > 0 && !hasClearedOutput));

  private eventBus$ = new ReplaySubject<any>();
  private eventBusMap = new Map();

  readonly loadNotebook$ = this.actions$
    .pipe(
      ofType(ClientActionType.LoadReportSuccess),
      switchMap((loadedReportAction) => this.loadNotebook(loadedReportAction.reportResource))
    )
    .subscribe();

  // Load embedded layout visuals for the editor
  readonly loadEmbeddedLayoutVisuals$ = this.actions$
    .pipe(
      ofType(ClientActionType.LoadReportSuccess),
      map((a) => (a.type === ClientActionType.LoadReportSuccess ? a : null)),
      filter((a) => !a.isEmbedded),
      switchMap((action) => {
        if (action.reportResource.hasEmbedded(LAYOUT_VIZ_REL)) {
          return this.api
            .getResources<NotebookVisualizationResource>(action.reportResource, LAYOUT_VIZ_REL)
            .then((vizResources) =>
              this.store.dispatch({
                type: CellActions.Type.LoadEmbeddedLayoutVisualsSuccess,
                vizResources,
              })
            )
            .catch((error) =>
              this.store.dispatch({
                type: CellActions.Type.LoadLayoutVisualsFailure,
                error,
              })
            );
        }

        return observableFrom([]);
      })
    )
    .subscribe();

  readonly loadAndInitializeNotbook$ = this.actions$
    .pipe(
      ofType(NotebookActions.Type.LoadFromSelectedReport),
      combineLatestWith(this.store.select(selectedReportResourceSelector).pipe(filter((r) => r != null))),
      switchMap(([action, reportResource]) => this.loadNotebook(reportResource).then(() => this.initNotebook()))
    )
    .subscribe();

  // Initialize notebook child objects
  readonly initializeNotebookContent$ = this.actions$
    .pipe(
      ofType(NotebookActions.Type.Init),
      withLatestFrom(
        this.store.select('client', 'notebooks', 'books', 'resources'),
        this.store.select('client', 'notebooks', 'books', 'initialized')
      ),
      filter(
        ([action, resources, initialized]) =>
          resources.count() > 0 && !initialized.get(resources.first({ token: null }).token)
      ),
      switchMap(([action, resources]) => {
        const notebook = resources.valueSeq().toArray()[0]; // odd stuff due to immutableJS
        if (notebook) {
          const socketSubscription = this.socketService.connect(notebook.pusher_channel_name);
          this.appendEventsToEventBus(socketSubscription.eventBus, notebook.pusher_channel_name);

          this.store.dispatch({
            type: NotebookActions.Type.InitComplete,
            notebookToken: notebook.token,
          });

          if (notebook.hasLink(CELLS_REL)) {
            return this.api
              .getResources<NotebookCellResource>(notebook, CELLS_REL, {
                includes: {
                  python_cells: [{ last_python_cell_run: 'python_cell_run_results' }, NEW_RUN_REL],
                },
              })
              .then((cells) =>
                this.store.dispatch({
                  type: CellActions.Type.LoadNotebookCellsSuccess,
                  notebookToken: notebook.token,
                  cellResources: cells,
                })
              )
              .catch((err) =>
                this.store.dispatch({
                  type: CellActions.Type.LoadNotebookCellsFailure,
                  notebookToken: notebook.token,
                  error: err,
                })
              );
          }
        }

        return observableFrom([]);
      })
    )
    .subscribe();

  // Initialize notebook child objects after loading with init or after add
  readonly connectToNotebookCellEvents$ = this.actions$
    .pipe(
      ofType(NotebookActions.Type.AddSuccess),
      switchMap((action) => {
        const socketSubscription = this.socketService.connect(action.notebookResource.pusher_channel_name);
        this.appendEventsToEventBus(socketSubscription.eventBus, action.notebookResource.pusher_channel_name);

        if (action.notebookResource.hasLink(CELLS_REL)) {
          // need to pull forms to allow running of cells
          return this.api
            .getResources<NotebookCellResource>(action.notebookResource, CELLS_REL, {
              includes: {
                python_cells: [{ last_python_cell_run: 'python_cell_run_results' }, NEW_RUN_REL],
              },
            })
            .then((cells) =>
              this.store.dispatch({
                type: CellActions.Type.LoadNotebookCellsSuccess,
                notebookToken: action.notebookResource.token,
                cellResources: cells,
              })
            )
            .catch((err) =>
              this.store.dispatch({
                type: CellActions.Type.LoadNotebookCellsFailure,
                notebookToken: action.notebookResource.token,
                error: err,
              })
            );
        }

        return observableFrom([]);
      })
    )
    .subscribe();

  // Load embedded last cell runs in cells
  readonly loadLastCellRun$ = this.actions$
    .pipe(
      ofType(CellActions.Type.LoadNotebookCellsSuccess),
      mergeMap((action) => action.cellResources),
      withLatestFrom(this.store.select(NotebookSelectors.runsResultTokensArray)),
      filter(([cell]) => cell.hasLink(LAST_RUN_REL))
    )
    .subscribe(([cell, runsResultTokens]) => {
      this.api
        .getResource<NotebookCellRunResource>(cell, LAST_RUN_REL)
        .then((run) =>
          this.store.dispatch({
            type: CellRunActions.Type.LoadLastCellRunSuccess,
            cellToken: cell.token,
            runToken: getTokenFromModeLink(run.links('python_cell_run')),
            resource: run,
            resultTokens: runsResultTokens.get(getTokenFromModeLink(run.links('python_cell_run'))),
          })
        )
        .catch((err) =>
          this.store.dispatch({
            type: CellRunActions.Type.LoadLastCellRunFailure,
            cellToken: cell.token,
            error: err,
          })
        );
    });

  constructor(
    private $rootScope: angular.IRootScopeService,
    private store: AppStore,
    private actions$: Observable<Actions>,
    private modeCollection: any,
    private ModeResource: any,
    private PythonSessionModel,
    private notebookModel: any,
    private cellModel: any,
    private api: DeprecatedApiService,
    private wms: WindowMessageService,
    private $http: angular.IHttpService,
    private $timeout: angular.ITimeoutService,
    private analytics: AnalyticsService,
    private $q: angular.IQService,
    private confirmDialogService: ConfirmDialogService,
    private socketService: SocketService,
    private featureFlagsFacade: FeatureFlagsFacade,
    private toast: ToastService,
    private switchViewFacade: SwitchViewFacade,
    private reportFacade: ReportFacade
  ) {
    // Initial page load cell run results linked from a run
    observableMerge(
      this.actions$.pipe(ofType(CellRunActions.Type.LoadLastCellRunSuccess)),
      this.actions$.pipe(ofType(CellRunActions.Type.LoadLayoutCellRunSuccess))
    )
      .pipe(
        switchMap((action) =>
          api
            .getResources<NotebookCellRunResultResource>(action.resource, RESULT_REL)
            .then((results) =>
              this.store.dispatch({
                type: CellRunActions.Type.LoadCellRunResultsSuccess,
                runToken: action.resource.token,
                resultResources: results,
              })
            )
            .catch((err) =>
              this.store.dispatch({
                type: CellRunActions.Type.LoadCellRunResultsFailure,
                runToken: action.resource.token,
                error: err,
              })
            )
        )
      )
      .subscribe();

    // TBR after feature flag remove for reusable dataset Start
    this.isRegisterImportedDatasetEnabled$ = this.featureFlagsFacade.asObservable(
      FeatureFlag.RegisterImportedDatasetsNotebooks
    );
    // TBR after feature flag remove for reusable dataset End

    this.isNotebookExportDatasetsEnabled$ = this.featureFlagsFacade.asObservable(FeatureFlag.NotebookExportDatasets);
    this.isNotebookDatasetsPublishedEnabled$ = this.featureFlagsFacade.asObservable(
      FeatureFlag.NotebookPublishExportedDatasets
    );

    // Load cell run from result resource from pusher
    this.actions$
      .pipe(
        ofType(CellRunActions.Type.LoadCellRunResultSuccess),
        withLatestFrom(this.isNotebookExportDatasetsEnabled$, store.select(NotebookSelectors.cellResourcesArray)),
        switchMap(([action, enabled, cellResources]) => {
          const result = action.resultResource;
          if (result.hasLink(CONTENT_REL) || result.hasEmbedded(CONTENT_REL)) {
            if (result.result_type === 'image' || result.result_type === 'csv') {
              this.store.dispatch({
                type: CellRunActions.Type.LoadCellRunResultContentSuccess,
                resultToken: result.token,
                content: result.links('web_result').href,
              });
              return observableFrom([result.links('web_result').href]);
            } else {
              return $http
                .get<string>(result.links(CONTENT_REL).href, {
                  responseType: 'text',
                  transformResponse: (val) => val,
                }) // override default transform to skip json deserializer
                .then((content) => {
                  const cell = cellResources.get(action.cellToken);
                  if (!!cell.local_dataset_exported_at && result.result_type === 'error' && enabled) {
                    this.store.dispatch({
                      type: CellActions.Type.RefreshDataViewAfterCellUpdate,
                      cellToken: action.cellToken,
                    });
                  }
                  this.store.dispatch({
                    type: CellRunActions.Type.LoadCellRunResultContentSuccess,
                    resultToken: result.token,
                    content: content.data,
                  });
                })
                .catch((err) =>
                  this.store.dispatch({
                    type: CellRunActions.Type.LoadCellRunResultContentFailure,
                    resultToken: result.token,
                    error: err,
                  })
                );
            }
          }

          return observableFrom([]);
        })
      )
      .subscribe();

    // Load cell run from pusher to maintain the cell-run -> result relationships
    this.actions$
      .pipe(
        ofType(CellRunActions.Type.LoadCellRunResultSuccess),
        switchMap((action) =>
          this.api
            .getResource<NotebookCellRunResource>(action.resultResource, CELL_RUN_REL)
            .then((res) =>
              this.store.dispatch({
                type: CellRunActions.Type.LoadCellRunFromBatchSuccess,
                resource: res,
                runToken: getTokenFromModeLink(res.links('python_cell_run')),
                cellToken: action.cellToken,
              })
            )
            .catch((err) =>
              this.store.dispatch({
                type: CellRunActions.Type.LoadLastCellRunFailure,
                error: err,
                cellToken: action.cellToken,
              })
            )
        )
      )
      .subscribe();

    // Initial page load of cell run results content
    this.actions$
      .pipe(
        ofType(CellRunActions.Type.LoadCellRunResultsSuccess),
        map((action) =>
          observableFrom(action.resultResources).pipe(
            map((result) =>
              observableDefer(() => {
                // if it's an image or csv, just link to it
                if (result.result_type === 'image' || result.result_type === 'csv') {
                  this.store.dispatch({
                    type: CellRunActions.Type.LoadCellRunResultContentSuccess,
                    resultToken: result.token,
                    content: result.links('web_result').href,
                  });
                  return createEmpty();
                } else {
                  if (result.links(CONTENT_REL)) {
                    return $http
                      .get<string>(result.links(CONTENT_REL).href, {
                        responseType: 'text',
                        transformResponse: (val) => val,
                      }) // override default transform to skip json deserializer
                      .then((content) => {
                        this.store.dispatch({
                          type: CellRunActions.Type.LoadCellRunResultContentSuccess,
                          resultToken: result.token,
                          content: content.data,
                        });
                        return createEmpty();
                      })
                      .catch((err) => {
                        this.store.dispatch({
                          type: CellRunActions.Type.LoadCellRunResultContentFailure,
                          resultToken: result.token,
                          error: err,
                        });
                        return createEmpty();
                      });
                  }
                }
              })
            )
          )
        ),
        mergeAll(),
        mergeMap((observable) => observable, null, CONCURRENT_CELLS_TO_LOAD)
      )
      .subscribe();

    // LoadLayoutVisuals must be fired after LoadReportRunSuccess has been dispatched
    this.actions$
      .pipe(
        ofType(CellActions.Type.LoadLayoutVisuals),
        withLatestFrom(this.store.select(selectedReportReportRunStateSelector)),
        switchMap(([action, reportRunState]) =>
          // Get report here since embedded report action may not have completed yet
          // Ideally, LoadLayoutVisuals could be triggered by a side-effect, but that would require the editor
          // to have a different set of actions for loading the report run vs the report view page
          this.api.getResource(reportRunState.resource, 'report').then((reportResource) =>
            this.api.getResources<NotebookVisualizationResource>(reportResource, LAYOUT_VIZ_REL).then((visuals) => ({
              visuals,
              reportResource,
              reportRunState,
            }))
          )
        ),
        filter(({ visuals, reportResource, reportRunState }) => visuals.length > 0),
        switchMap(({ visuals, reportResource, reportRunState }) => {
          this.store.dispatch({
            type: CellActions.Type.StubLayoutVisuals,
            vizResources: visuals,
          });

          // Note that even though the ReportRun is re-fetched from the server, the
          // updated ReportRun resource is never pushed into the Rx state store.
          // This is because currently, most of the codebase assumes that ReportRun
          // only updates in state during initial load or when loading a new report
          // run after re-running a report, so unconditional action side effects
          // are used to re-render the whole report, which we definitely don't want
          // to do on every poll or even just once when we finish polling. The
          // exception is query run polling, which doesn't update ReportRun in
          // state either and instead just fetches the query run off the updated
          // ReportRun and pushes that into state, same as this code does for
          // Python visualizations fetched off the updated ReportRun.
          //
          // I [Han] think the Right Thing would be for a unified ReportRun polling
          // service that always updates ReportRun in state, and then rather than
          // unconditional action side effects to render the report, instead renders
          // the report conditional on whether things in the report changed, renders
          // charts conditional on whether the query runs changed, and renders Python
          // visualizations conditional on whether the Python notebook finished.
          // But that's a project for another day.
          return new PollingObservable(
            {
              min: MIN_POLL_INTERVAL,
              max: MAX_POLL_INTERVAL,
              factor: POLL_FACTOR,
            },
            observifyAngularPromise(() =>
              this.api.getSelf(reportRunState.resource, {
                includes: {
                  python_cell_runs: {
                    python_cell_runs: 'python_cell_run_results',
                  },
                },
              })
            )
          ).pipe(
            takeUntil(
              observableMerge(
                this.actions$.pipe(ofType(CellActions.Type.LoadLayoutVisualsReady)),
                this.actions$.pipe(ofType(CellActions.Type.LoadLayoutVisualsFailure)),
                this.actions$.pipe(ofType(CellActions.Type.LoadLayoutVisualsSkipped))
              )
            ),
            map(
              (tickValue) =>
                ({
                  type: CellActions.Type.PolledLayoutVisuals,
                  report: reportResource,
                  reportRun: tickValue.value,
                }) as CellActions.PolledLayoutCells
            )
          );
        })
      )
      .subscribe(
        (action) => this.store.dispatch(action),
        (err) =>
          this.store.dispatch({
            type: CellActions.Type.LoadLayoutVisualsFailure,
            error: err,
          })
      );

    this.actions$
      .pipe(
        ofType(CellActions.Type.PolledLayoutVisuals),
        switchMap(({ report, reportRun }) => {
          // check if already in a terminal python_state
          if (reportRun.python_state === 'failed') {
            this.store.dispatch({
              type: CellActions.Type.LoadLayoutVisualsFailure,
              error: new Error(`python_state on report run returned as 'failed' by server`),
            });
          } else if (reportRun.python_state === 'succeeded' || reportRun.python_state === 'none') {
            this.store.dispatch({
              type: CellActions.Type.LoadLayoutVisualsReady,
            });

            if (!report.hasLink(LAYOUT_VIZ_REL)) {
              this.store.dispatch({
                type: CellActions.Type.LoadLayoutVisualsSuccess,
                reportRun,
                vizResources: [],
              });
            } else {
              // Reload the report notebook visualizations since they may have changed
              return this.api
                .getResources<NotebookVisualizationResource>(report, LAYOUT_VIZ_REL, {
                  includes: {
                    [LAYOUT_VIZ_REL]: { [CELL_RUN_REL]: RESULT_REL },
                  },
                })
                .then((vizResources) =>
                  this.store.dispatch({
                    type: CellActions.Type.LoadLayoutVisualsSuccess,
                    reportRun,
                    vizResources,
                  })
                )
                .catch((error) =>
                  this.store.dispatch({
                    type: CellActions.Type.LoadLayoutVisualsFailure,
                    error,
                  })
                );
            }
          } else if (reportRun.python_state === 'skipped') {
            this.store.dispatch({
              type: CellActions.Type.LoadLayoutVisualsSkipped,
            });
          }
          return [];
        })
      )
      .subscribe();

    // On report run View page, load layout cells from python_visualizations after finished polling for notebook to finish running
    this.actions$
      .pipe(
        ofType(CellActions.Type.LoadLayoutVisualsSuccess),
        switchMap((action) => {
          for (const v of action.vizResources) {
            const cellToken = getTokenFromModeLink(v.links(CELL_REL));
            store.dispatch({
              type: CellActions.Type.LoadVizCellSuccess,
              vizToken: v.token,
              cellToken,
            });
          }

          // cell runs from the time when the report was run
          return api
            .getResources<NotebookCellRunResource>(action.reportRun, REPORT_CELL_RUNS_REL)
            .then((cellRuns) => {
              for (const cellRun of cellRuns) {
                const cellToken = getTokenFromModeLink(cellRun.links(CELL_REL));
                const runToken = getTokenFromModeLink(cellRun.links(LAST_RUN_REL));
                store.dispatchThunk(
                  NotebookSelectors.vizCellRunTokensArray,
                  NotebookSelectors.runsResultTokensArray,
                  (oldRunsTokenArray, oldResultTokens) => ({
                    type: CellRunActions.Type.LoadLayoutCellRunSuccess,
                    cellToken,
                    runToken,
                    resource: cellRun,
                    oldRunToken: oldRunsTokenArray.get(cellToken),
                    oldResultTokens: oldRunsTokenArray.get(cellToken)
                      ? oldResultTokens.get(oldRunsTokenArray.get(cellToken))
                      : null,
                  })
                );
              }
            })
            .catch((error) => {
              store.dispatch({
                type: CellActions.Type.LoadLayoutVisualsFailure,
                error,
              });
            });
        })
      )
      .subscribe();

    // In editor, Load Embedded Layout Cells from python_visualizations
    this.actions$
      .pipe(
        ofType(CellActions.Type.LoadEmbeddedLayoutVisualsSuccess),
        withLatestFrom(store.select(NotebookSelectors.runsResultTokensArray)),
        switchMap(([action, runsResultTokens]) =>
          observableMerge(
            action.vizResources.map((v) => {
              const cellToken = getTokenFromModeLink(v.links(CELL_REL));
              const runToken = getTokenFromModeLink(v.links(LAST_RUN_REL));
              this.store.dispatch({
                type: CellActions.Type.LoadVizCellSuccess,
                vizToken: v.token,
                cellToken,
              });

              // embedded run will be in the layout viz, not the embedded cell
              return api
                .getResource<NotebookCellRunResource>(v, CELL_RUN_REL)
                .then((run) =>
                  this.store.dispatch({
                    type: CellRunActions.Type.LoadLastCellRunSuccess,
                    cellToken: cellToken,
                    runToken: runToken,
                    resource: run,
                    resultTokens: runsResultTokens.get(runToken),
                  })
                )
                .catch((err) =>
                  this.store.dispatch({
                    type: CellRunActions.Type.LoadLastCellRunFailure,
                    cellToken: cellToken,
                    error: err,
                  })
                );
            })
          )
        )
      )
      .subscribe();

    // Patch notebook
    this.actions$
      .pipe(
        ofType(NotebookActions.Type.UpdateType),
        withLatestFrom(store.select(NotebookSelectors.notebookResourcesArray)),
        switchMap(([action, notebookResources]) => {
          const notebook = notebookResources.get(action.notebookToken);

          if (notebook) {
            // embedding the 'new_python_session' resource is not an optimization, without it
            // this.PythonSessionModel.create() (called later down the line by InitSession's
            // side effect) rejects promise with error 'Create form not found'
            return notebook
              .forms('edit')
              .$submit({
                'python_notebook.notebook_type': action.notebookType,
                includes: ['new_python_session', 'new_python_cell_batch_run', 'new_python_cell'],
              })
              .then((notebookResource) => {
                store.dispatch({
                  type: NotebookActions.Type.UpdateSuccess,
                  notebookResource,
                });
              })
              .catch((err) => {
                this.toast.error('Error updating the Notebook environment, try switching again');
                store.dispatch({
                  type: NotebookActions.Type.UpdateTypeFailure,
                  error: err,
                  original: notebook,
                });
              });
          }

          return createEmpty();
        })
      )
      .subscribe();

    // After patching, start new session
    this.actions$.pipe(ofType(NotebookActions.Type.UpdateSuccess)).subscribe(({ notebookResource: { token } }) => {
      this.initSession(token, false);
    });

    // Add notebook
    this.actions$
      .pipe(
        ofType(NotebookActions.Type.Add),
        switchMap((action) =>
          this.notebookModel
            .create({
              report: action.reportToken,
              params: {
                'python_notebook.doc_cells': true,
                includes: [
                  'perspective_python_sessions',
                  'python_cells',
                  'new_python_cell',
                  'new_python_session',
                  'new_python_cell_batch_run',
                ],
              },
            })
            .then((res) =>
              this.store.dispatch({
                type: NotebookActions.Type.AddSuccess,
                notebookResource: res._resource,
              })
            )
            .catch((err) =>
              this.store.dispatch({
                type: NotebookActions.Type.AddFailure,
                error: err,
                reportToken: action.reportToken,
              })
            )
        )
      )
      .subscribe();

    // Create a cell
    this.actions$
      .pipe(
        ofType(CellActions.Type.AddCell),
        withLatestFrom(store.select(NotebookSelectors.selectedNotebookResource)),
        switchMap(([action, notebook]) => {
          const params = {
            'python_cell.content': action.content || '',
            'python_cell.content_type': action.contentType || 'python',
            includes: NEW_RUN_REL,
          };
          if (action.nextToken) {
            params['python_cell.next_cell_token'] = action.nextToken;
          }

          return this.api
            .create<NotebookCellResource>(notebook, NEW_CELL_REL, params)
            .then((res) =>
              this.store.dispatch({
                type: CellActions.Type.AddCellSuccess,
                cellResource: res,
                notebookToken: action.notebookToken,
                nextToken: action.nextToken,
              })
            )
            .catch((err) =>
              this.store.dispatch({
                type: CellActions.Type.AddCellFailure,
                error: err,
                notebookToken: action.notebookToken,
              })
            );
        })
      )
      .subscribe();

    // Create a cell and run
    // TODO: Consolidate with add cell side-effect
    this.actions$
      .pipe(
        ofType(CellActions.Type.AddCellAndRun),
        withLatestFrom(store.select(NotebookSelectors.selectedNotebookResource)),
        switchMap(([action, notebook]) => {
          const params = {
            'python_cell.content': action.content,
            'python_cell.content_type': 'python',
            includes: NEW_RUN_REL,
          };
          if (action.nextToken) {
            params['python_cell.next_cell_token'] = action.nextToken;
          }
          return this.api
            .create<NotebookCellResource>(notebook, NEW_CELL_REL, params)
            .then((res) => {
              this.store.dispatch({
                type: CellActions.Type.AddCellSuccess,
                cellResource: res,
                notebookToken: action.notebookToken,
                nextToken: action.nextToken,
              });
              this.runCell(action.notebookToken, res.token);
            })
            .catch((err) =>
              this.store.dispatch({
                type: CellActions.Type.AddCellFailure,
                error: err,
                notebookToken: action.notebookToken,
              })
            );
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(CellActions.Type.MoveCell),
        withLatestFrom(
          store.select(NotebookSelectors.selectedNotebookCellTokens),
          store.select(NotebookSelectors.cellResourcesArray),
          store.select(NotebookSelectors.selectedNextCellToken)
        ),
        switchMap(([action, cellTokens, resByCell, originalNextToken]) => {
          let resource = resByCell.get(action.cellToken);
          if (resource && cellTokens) {
            // remember that the cell tokens will be in the new moved order at this point
            const tokenArray = cellTokens.toArray();
            const index = tokenArray.indexOf(action.cellToken);
            let nextToken = tokenArray[index + 1];

            if (action.down) {
              // take the now previous cell and set its next cell token to the current cell
              resource = resByCell.get(tokenArray[index - 1]);
              nextToken = action.cellToken;
            }

            if (index >= 0 && nextToken) {
              return this.updateCell(resource, { next_cell_token: nextToken })
                .then((res) =>
                  this.store.dispatch({
                    type: CellActions.Type.UpdateCellSuccess,
                    cellResource: res,
                  })
                )
                .catch((err) =>
                  this.store.dispatch({
                    type: CellActions.Type.MoveCellFailure,
                    error: err,
                    cellToken: action.cellToken,
                    nextCellToken: originalNextToken,
                    notebookToken: action.notebookToken,
                  })
                );
            }
          }

          return createEmpty();
        })
      )
      .subscribe();

    // Update Cell Content
    this.actions$
      .pipe(
        ofType(CellActions.Type.UpdateCell),
        withLatestFrom(
          store.select(NotebookSelectors.cellResourcesArray),
          store.select(NotebookSelectors.cellContentsArray),
          store.select(NotebookSelectors.cellContentTypesArray),
          (action, resByCell, contentByCell, typeByCell) => ({
            cellToken: action.cellToken,
            resource: resByCell.get(action.cellToken),
            content: contentByCell.get(action.cellToken),
            content_type: typeByCell.get(action.cellToken),
          })
        ),
        filter(
          (update) => update.resource.content !== update.content || update.resource.content_type !== update.content_type
        ),
        switchMap((update) =>
          this.updateCell(update.resource, {
            content: update.content,
            content_type: update.content_type,
          })
            .then((res) =>
              this.store.dispatch({
                type: CellActions.Type.UpdateCellSuccess,
                cellResource: res,
              })
            )
            .catch((err) =>
              this.store.dispatch({
                type: CellActions.Type.UpdateCellFailure,
                error: err,
                cellToken: update.cellToken,
                original: update.resource,
              })
            )
        )
      )
      .subscribe();

    // Delete a cell
    this.actions$
      .pipe(
        ofType(CellActions.Type.DestroyCell),
        withLatestFrom(
          this.store.select(NotebookSelectors.cellResourcesArray),
          this.store.select(NotebookSelectors.notebookCellTokensArray),
          this.store.select(NotebookSelectors.cellLastRunTokensArray),
          this.store.select(NotebookSelectors.vizCellTokensArray),
          this.store.select(NotebookSelectors.runsResultTokensArray)
        ),
        switchMap(([action, cells, cellTokensByNotebook, cellLastRunTokens, vizCellTokens, runsResultTokens]) => {
          const cell = cells.get(action.cellToken);
          const runToken = cellLastRunTokens.get(action.cellToken);
          const vizToken = vizCellTokens.get(action.cellToken);
          const resultTokens = runsResultTokens.get(runToken);
          if (cell) {
            return cell
              .forms('destroy')
              .$submit()
              .then(() => {
                this.store.dispatch({
                  type: CellActions.Type.RemoveFromLayout,
                  cellToken: action.cellToken,
                  vizToken,
                });

                this.store.dispatch({
                  type: CellActions.Type.DestroyCellSuccess,
                  cellToken: action.cellToken,
                  notebookToken: action.notebookToken,
                  runToken,
                  resultTokens,
                });

                // check if last cell and add a new one if that's the case
                if (cellTokensByNotebook.get(action.notebookToken).isEmpty()) {
                  this.store.dispatch({
                    type: CellActions.Type.AddCell,
                    notebookToken: action.notebookToken,
                  });
                }

                if (cell.local_dataset_exported_at) {
                  this.store.dispatch({
                    type: CellActions.Type.RefreshDataViewAfterCellUpdate,
                    cellToken: action.cellToken,
                  });
                }
              })
              .catch((err) =>
                this.store.dispatch({
                  type: CellActions.Type.DestroyCellFailure,
                  cellToken: action.cellToken,
                  notebookToken: action.notebookToken,
                  error: err,
                })
              );
          }

          return createEmpty();
        })
      )
      .subscribe();

    // Remove All Cell Outputs
    this.actions$
      .pipe(
        ofType(NotebookActions.Type.ClearAllCellOutputs),
        withLatestFrom(
          store.selectWithParam(
            NotebookSelectors.cellRunResultTokensByNotebook,
            store.select(NotebookSelectors.selectedNotebookToken)
          )
        ),
        map(([action, cellRunTokensByNotebook]) => cellRunTokensByNotebook.toArray() as Array<string>),
        tap((noteBookCellRunTokens) => {
          // Dispatch an action to clear the cell output
          noteBookCellRunTokens.forEach((cellToken) => {
            this.store.dispatch({
              type: CellActions.Type.ClearCellOutput,
              cellToken,
            });
          });

          this._hasClearedOutput$.next(true);
        })
      )
      .subscribe();

    // Freeze cell
    this.actions$
      .pipe(
        ofType(CellActions.Type.ToggleFreezeCell),
        withLatestFrom(
          store.select(NotebookSelectors.cellFrozenArray),
          store.select(NotebookSelectors.cellResourcesArray)
        ),
        switchMap(([action, frozenByCell, resByCell]) => {
          if (resByCell.has(action.cellToken)) {
            return this.updateCell(resByCell.get(action.cellToken), {
              frozen_in_layout: frozenByCell.get(action.cellToken),
            })
              .then((res) =>
                this.store.dispatch({
                  type: CellActions.Type.UpdateCellSuccess,
                  cellResource: res,
                })
              )
              .catch((err) =>
                this.store.dispatch({
                  type: CellActions.Type.ToggleFreezeCellFailure,
                  error: err,
                  cellToken: action.cellToken,
                })
              );
          }

          return createEmpty();
        })
      )
      .subscribe();

    // Toggle Cell Content Type
    this.actions$
      .pipe(
        ofType(CellActions.Type.UpdateContentType),
        withLatestFrom(
          store.select(NotebookSelectors.cellResourcesArray),
          store.select(NotebookSelectors.vizCellTokensArray)
        ),
        filter(([action, resByCell, _]) => resByCell.has(action.cellToken)),
        switchMap(([action, resByCell, vizByCell]) =>
          from(
            this.updateCell(resByCell.get(action.cellToken), {
              content_type: action.contentType,
              frozen_in_layout: false,
            })
          ).pipe(
            tap((_r) =>
              this.store.dispatch({
                type: CellActions.Type.RemoveFromLayout,
                cellToken: action.cellToken,
                vizToken: vizByCell.get(action.cellToken),
              })
            ),
            tap((cellResource: NotebookCellResource) => {
              this.store.dispatch({
                type: CellActions.Type.UpdateCellSuccess,
                cellResource,
              });
            }),
            catchError((error) => {
              this.store.dispatch({
                type: CellActions.Type.UpdateContentTypeFailure,
                error,
                cellToken: action.cellToken,
                original: resByCell.get(action.cellToken),
              });

              return createEmpty();
            })
          )
        )
      )
      .subscribe();

    // Add cell to layout
    this.actions$
      .pipe(
        ofType(CellActions.Type.AddToLayout),
        withLatestFrom(store.select(selectedReportResourceSelector)),
        switchMap(([action, report]) =>
          this.api
            .create<NotebookVisualizationResource>(report, NEW_LAYOUT_VIZ_REL, {
              'report_layout_python_cell.python_cell.token': action.cellToken,
            })
            .then((res) => {
              this.store.dispatch({
                type: CellActions.Type.AddToLayoutSuccess,
                vizResource: res,
                cellToken: action.cellToken,
              });
            })
            .catch((err) =>
              this.store.dispatch({
                type: CellActions.Type.AddToLayoutFailure,
                error: err,
                cellToken: action.cellToken,
              })
            )
        )
      )
      .subscribe();

    // Remove cell from layout
    this.actions$
      .pipe(
        ofType(CellActions.Type.RemoveFromLayout),
        withLatestFrom(
          store.select('client', 'selectedReport', 'notebookViz', 'cellTokens'),
          store.select('client', 'selectedReport', 'notebookViz', 'resources')
        ),
        switchMap(([action, cellTokens, vizResources]) => {
          const vizToken = cellTokens.findKey((cellToken) => cellToken === action.cellToken);
          if (!vizToken) return createEmpty();

          const vizResource = vizResources.get(vizToken);
          if (!vizResource) return createEmpty();

          return vizResource
            .forms('destroy')
            .$submit()
            .then((_) =>
              this.store.dispatch({
                type: CellActions.Type.RemoveFromLayoutSuccess,
                cellToken: action.cellToken,
                vizToken: vizToken,
              })
            )
            .catch((error) =>
              this.store.dispatch({
                type: CellActions.Type.RemoveFromLayoutFailure,
                cellToken: action.cellToken,
                vizToken: vizToken,
                error,
              })
            ) as angular.IPromise<void>;
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(CellActions.Type.RemoveFromLayoutViaBuilder),
        withLatestFrom(
          store.select('client', 'selectedReport', 'notebookViz', 'cellTokens'),
          store.select('client', 'selectedReport', 'notebookViz', 'resources')
        ),
        switchMap(([action, cellTokens, vizResources]) => {
          const vizToken = action.vizToken;
          const vizResource = vizResources.get(vizToken);
          if (vizToken && vizResource) {
            return vizResource
              .forms('destroy')
              .$submit()
              .then(() => {
                this.store.dispatch({
                  type: CellActions.Type.RemoveFromLayoutSuccess,
                  cellToken: cellTokens.get(vizToken),
                  vizToken: vizToken,
                });
              })
              .catch((err) =>
                this.store.dispatch({
                  type: CellActions.Type.RemoveFromLayoutFailure,
                  cellToken: cellTokens.get(vizToken),
                  vizToken: vizToken,
                  error: err,
                })
              );
          }

          return createEmpty();
        })
      )
      .subscribe();

    // TBR after feature flag remove for reusable dataset Start
    this.actions$
      .pipe(
        ofType(SwitchViewActions.loadImportedDatasetSuccess.type),
        withLatestFrom(store.select(NotebookSelectors.selectedNotebookSession), this.isRegisterImportedDatasetEnabled$),
        filter(([, session, enabled]) => session && enabled),
        map(
          ([action, session]) =>
            ({
              type: NotebookActions.Type.VerifySession,
              runToken: action.mostRecentReportRunToken,
              session,
            }) as Actions
        )
      )
      .subscribe((action) => store.dispatch(action));

    this.actions$
      .pipe(
        ofType(SwitchViewActions.datasetAllRefreshed.type),
        withLatestFrom(store.select(NotebookSelectors.selectedNotebookSession), this.isRegisterImportedDatasetEnabled$),
        filter(([, session, enabled]) => session && enabled),
        switchMap(([action, session]) => {
          this.store.dispatch({
            type: NotebookActions.Type.VerifySession,
            runToken: action.mostRecentReportRunToken,
            session,
          });
          this.store.dispatch({
            type: NotebookActions.Type.RefreshSessionReportRun,
            notebookToken: action.mostRecentReportRunToken,
          });
          return createEmpty();
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(SwitchViewActions.removeDatasetFromSideNav),
        switchMap((action) =>
          combineLatest([
            this.store.select(NotebookSelectors.selectedNotebookSession),
            this.switchViewFacade.getEnhancedDataseResult(action.switchViewToken),
            this.isRegisterImportedDatasetEnabled$,
          ]).pipe(
            filter(
              ([session, result, enabled]) => session && result.producerType !== ProducerTypes.NotebookCell && enabled
            ),
            map(
              ([session]) =>
                ({
                  type: NotebookActions.Type.VerifySession,
                  runToken: action.switchViewToken,
                  session,
                }) as Actions
            )
          )
        )
      )
      .subscribe((action) => store.dispatch(action));
    // TBR after feature flag remove for reusable dataset End

    this.actions$
      .pipe(
        ofType(CellExportActions.exportCellSuccess),
        switchMap((action) => {
          this.store.dispatch({
            type: CellActions.Type.ExportCellComplete,
            cellToken: action.cellToken,
            queryToken: action.queryToken,
            switchViewToken: action.switchViewToken,
            localDatasetExportedAt: action.localDatasetExportedAt,
          });
          return createEmpty();
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(SwitchViewActions.removeDatasetSuccess),
        switchMap((action) => {
          if (action.producerToken) {
            this.store.dispatch({
              type: CellActions.Type.ExportedCellDelete,
              cellToken: action.producerToken,
            });
          }
          return createEmpty();
        })
      )
      .subscribe();

    // Update viz title
    this.actions$
      .pipe(
        ofType(CellActions.Type.UpdateVizTitle),
        withLatestFrom(
          store.select('client', 'selectedReport', 'notebookViz', 'resources'),
          store.select('client', 'selectedReport', 'notebookViz', 'cellTokens')
        ),
        switchMap(([action, resByViz, cellTokenByViz]) => {
          const resource = resByViz.get(action.vizToken);
          if (resource) {
            return resource
              .forms('edit')
              .$submit({
                'report_layout_python_cell.title': action.title,
              })
              .then((res) =>
                this.store.dispatch({
                  type: CellActions.Type.UpdateVizSuccess,
                  vizResource: res,
                })
              )
              .catch((err) =>
                this.store.dispatchThunk(NotebookSelectors.vizResourcesArray, (vizResourcesArray) => ({
                  type: CellActions.Type.UpdateVizTitleFailure,
                  error: err,
                  vizToken: action.vizToken,
                  vizResourcesArray,
                }))
              );
          }
          return createEmpty();
        })
      )
      .subscribe();

    // Open existing notebook session or create a new one
    this.actions$
      .pipe(
        ofType(NotebookActions.Type.InitSession),
        withLatestFrom(
          this.store.select('client', 'notebooks', 'books'),
          this.store.select(selectedReportLastSuccessfulRunTokenSelector)
        ),
        switchMap(([action, notebooks, runToken]) => {
          const notebookResource = notebooks.resources.get(action.notebookToken);
          const session = notebooks.sessions.get(action.notebookToken);
          const channelName = notebooks.pusherChannelNames.get(action.notebookToken);

          // Connect to pusher in case not done already
          const socketSubscription = this.socketService.connect(channelName);
          this.appendEventsToEventBus(socketSubscription.eventBus, channelName);

          if (notebookResource.hasLink(SESSIONS_REL)) {
            // Function to retrieve the current active session, if any
            const getActiveSession = () =>
              this.api
                .getResources<SessionResource>(notebookResource, SESSIONS_REL, {
                  relationAs: 'python_sessions',
                })
                .then((sessions) => {
                  const activeSession = sessions.find((s) => s.active);
                  if (activeSession) {
                    this.store.dispatch({
                      type: NotebookActions.Type.LoadSessionSuccess,
                      notebookToken: notebookResource.token,
                      sessionResource: activeSession,
                      runToken,
                    });
                  } else {
                    return this.api
                      .create<SessionResource>(notebookResource, NEW_SESSION_REL, {})
                      .then((newSession) =>
                        this.store.dispatch({
                          type: NotebookActions.Type.LoadSessionSuccess,
                          notebookToken: notebookResource.token,
                          sessionResource: newSession,
                          runToken,
                        })
                      )
                      .catch((err) =>
                        this.store.dispatch({
                          type: NotebookActions.Type.InitSessionFailure,
                          notebookToken: notebookResource.token,
                          error: err,
                        })
                      );
                  }
                });

            // If there's an existing session, destroy it
            // TODO: If the backend supports deactivating sessions that need to be terminated, then check the reuseIfActive flag
            if (session && session.active) {
              return session
                .forms('destroy')
                .$submit()
                .then(getActiveSession)
                .catch((err) =>
                  this.store.dispatch({
                    type: NotebookActions.Type.InitSessionFailure,
                    notebookToken: notebookResource.token,
                    error: err,
                  })
                );
            }

            return getActiveSession();
          }

          return observableFrom([]);
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(NotebookActions.Type.RefreshSessionReportRun),
        withLatestFrom(
          store.select(selectedReportReportRunStateSelector),
          store.select(NotebookSelectors.selectedNotebookSession)
        ),
        switchMap(([action, runState, session]) =>
          session
            .forms('load_report_run')
            .$submit({
              'report_run.token': runState.token,
            })
            .catch((err) =>
              // let's just say the session is terminated to get the user to restart it if we can't load data into it
              this.store.dispatch({
                type: NotebookActions.Type.Terminated,
                terminationReason: err.message,
                sessionToken: session.token,
                notebookToken: action.notebookToken,
              })
            )
        )
      )
      .subscribe();

    // After polling a report run, verify that the current session is still valid
    this.actions$
      .pipe(
        ofType(ReportRunActionType.PollReportRunComplete),
        withLatestFrom(
          store.select(NotebookSelectors.selectedNotebookSession),
          store.select(NotebookSelectors.selectedNotebookToken)
        ),
        filter(([, session, notebookToken]) => session != null && notebookToken != null),
        map(
          ([action, session, notebookToken]) =>
            ({
              type: NotebookActions.Type.VerifySession,
              runToken: action.token,
              session,
            }) as Actions
        )
      )
      .subscribe((action) => store.dispatch(action));

    // After loading a session, start polling it if it's active and load in the latest reportRun, if any
    this.actions$
      .pipe(
        ofType(NotebookActions.Type.LoadSessionSuccess),
        filter((action) => action.sessionResource.active),
        withLatestFrom(store.select(selectedReportReportRunStateSelector)),
        switchMap(([action, reportRunState]) => {
          this.store.dispatch({
            type: NotebookActions.Type.PollSession,
            notebookToken: action.notebookToken,
          });

          // Load newest report run if this is a new session
          if (reportRunState && NotebookHelpers.isSessionEmpty(action.sessionResource)) {
            return action.sessionResource
              .forms('load_report_run')
              .$submit({
                'report_run.token': reportRunState.token,
              })
              .catch((err) =>
                // let's just say the session is terminated to get the user to restart it if we can't load data into it
                this.store.dispatch({
                  type: NotebookActions.Type.Terminated,
                  terminationReason: err.message,
                  sessionToken: action.sessionResource.token,
                  notebookToken: action.notebookToken,
                })
              );
          }

          return createEmpty();
        })
      )
      .subscribe();

    // Poll for sesssion deactivation
    this.actions$
      .pipe(
        ofType(NotebookActions.Type.PollSession),
        withLatestFrom(
          this.store.select('client', 'notebooks', 'books', 'sessions'),
          this.store.select(selectedReportLastSuccessfulRunTokenSelector)
        ),
        switchMap(([action, sessions, runToken]) => {
          let disconnected = false;
          const session = sessions.get(action.notebookToken);

          return observableInterval(30000).pipe(
            takeWhile((_) => !disconnected),
            switchMap(() =>
              this.api.getSelf(session).then((newSession) => {
                if (!newSession.active) {
                  disconnected = true;
                  this.store.dispatch({
                    type: NotebookActions.Type.LoadSessionSuccess,
                    notebookToken: action.notebookToken,
                    sessionResource: newSession,
                    runToken,
                  });
                }
              })
            ),
            catchError((err) => {
              this.store.dispatch({
                type: NotebookActions.Type.NoSession,
                notebookToken: action.notebookToken,
                error: err,
              });

              return createEmpty();
            })
          );
        })
      )
      .subscribe();

    // Change the raw repospone to mode resource from ngrx store and set the report and report run
    this.actions$.pipe(ofType(NotebookActions.Type.AddNotebookResourcesOnSuccess)).subscribe((action) => {
      const notebookResources = new this.ModeResource(action.notebookResource);
      const reportRunResource = new this.ModeResource(notebookResources.embedded('last_report_run'));
      const reportResource = new this.ModeResource(notebookResources.embedded('report'));

      // Updating report header data for SQL-free notebooks
      this.reportFacade.updateHeaderReportDataManually(reportResource);
      this.store.dispatch({
        type: NotebookActions.Type.SaveReport,
        reportRun: reportRunResource,
        report: reportResource,
      });

      this.store.dispatch({
        type: NotebookActions.Type.AddSuccess,
        notebookResource: notebookResources,
      });
    });

    // Init session after notebook add success and mark it for run
    this.actions$.pipe(ofType(NotebookActions.Type.AddSuccess)).subscribe((action) => {
      this.store.dispatch({
        type: NotebookActions.Type.InitSession,
        notebookToken: action.notebookResource.token,
        reuseIfActive: false,
        runNewCells: true,
      });
    });

    // When Markdown is run update/preserve the contents
    this.actions$
      .pipe(
        ofType(NotebookActions.Type.Run),
        filter((action) => action.cellTokensToRun != null && action.cellTokensToRun.length > 0),
        withLatestFrom(
          store.selectWithParam(
            NotebookSelectors.cellsByNotebook,
            store.select(NotebookSelectors.selectedNotebookToken)
          )
        ),
        mergeMap(([action, cells]) =>
          observableFrom(
            cells.filter(
              (cell) =>
                cell.content_type === NotebookTypes.ContentType.Markdown &&
                cell.content &&
                action.cellTokensToRun.includes(cell.token)
            )
          )
        )
      )
      .subscribe(({ token }) => this.saveCell(token));

    // Run selected cells in notebook
    this.actions$
      .pipe(
        ofType(NotebookActions.Type.Run),
        filter((action) => !action.runAllCells),
        withLatestFrom(
          store.select(NotebookSelectors.selectedNotebookSession),
          store.selectWithParam(
            NotebookSelectors.cellsByNotebook,
            store.select(NotebookSelectors.selectedNotebookToken)
          ),
          store.select(NotebookSelectors.runsResultTokensArray)
        ),
        switchMap(([action, session, cells, runsResultTokens]) => {
          if (session && session.active && cells) {
            // Note, this code is currently assuming we'll only ever run one cell in a batch.
            // Would only require some minor work to support real batch running.

            // don't run cells that are empty
            return observableMerge(
              cells
                .filter(
                  (cell) =>
                    cell.content_type === NotebookTypes.ContentType.Python &&
                    cell.content &&
                    action.cellTokensToRun.indexOf(cell.token) >= 0
                )
                .map((cell) => {
                  if (cell.resource.hasEmbedded(NEW_RUN_REL)) {
                    return this.api
                      .create<NotebookCellRunResource>(cell.resource, NEW_RUN_REL, {
                        'python_cell.content': cell.content,
                        'python_cell.content_type': cell.content_type,
                        'python_cell.python_session.token': session.token,
                      })
                      .then((run) => {
                        this.store.dispatch({
                          type: CellRunActions.Type.CreateRunSuccess,
                          cellToken: cell.token,
                          runToken: cell.lastRunToken,
                          resource: run,
                          resultTokens: runsResultTokens.get(cell.lastRunToken),
                        });
                      })
                      .catch((err) =>
                        this.store.dispatch({
                          type: CellActions.Type.RunFailed,
                          notebookToken: action.notebookToken,
                          cellToken: cell.token,
                          error: err,
                        })
                      );
                  }

                  this.store.dispatch({
                    type: CellActions.Type.RunFailed,
                    notebookToken: action.notebookToken,
                    cellToken: cell.token,
                    error: new Error('No new run form in cell'),
                  });
                  console.error('No new run form in cell', cell.token);
                  return createEmpty();
                })
            );
          } else {
            this.store.dispatch({
              type: NotebookActions.Type.NoSession,
              notebookToken: action.notebookToken,
            });
          }

          return observableFrom([]);
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(NotebookActions.Type.CancelRun),
        withLatestFrom(store.select(NotebookSelectors.selectedNotebookSession)),
        switchMap(([_, session]) => {
          this.isCancelling = true;

          return session.forms('cancel_run').$submit();
        })
      )
      .subscribe();

    // Run notebook
    this.actions$
      .pipe(
        ofType(NotebookActions.Type.Run),
        filter((action) => action.runAllCells),
        withLatestFrom(
          store.select(NotebookSelectors.selectedNotebookResource),
          store.select(NotebookSelectors.selectedNotebookSession),
          store.selectWithParam(
            NotebookSelectors.cellRunResultTokensByNotebook,
            store.select(NotebookSelectors.selectedNotebookToken)
          )
        ),
        switchMap(([action, resource, session, resultTokens]) => {
          if (resource && session && session.active) {
            return this.api
              .create(resource, NEW_BATCH_RUN_REL, {
                'python_cell_batch_run.python_session.token': session.token,
                'python_cell_batch_run.run_all': true,
                includes: {
                  python_cells: { python_cells: 'last_python_cell_run' },
                },
              })
              .then((batchRes) => {
                this.cancellableRunTokens = new Set(batchRes.embedded('cell_runs').map((x) => x.token));

                this.store.dispatch({
                  type: NotebookActions.Type.CreateRunSuccess,
                  runToken: getTokenFromModeLink(resource.links('python_cell_run')),
                  notebookToken: action.notebookToken,
                  resultTokens,
                });

                if (batchRes.hasEmbedded(CELL_RUNS_REL)) {
                  // can't use api service since the response doesn't conform
                  const runs = batchRes.embedded(CELL_RUNS_REL) as NotebookCellRunResource[];
                  for (const runResource of runs) {
                    this.store.dispatch({
                      type: CellRunActions.Type.LoadCellRunFromBatchSuccess,
                      cellToken: getTokenFromModeLink(runResource.links(CELL_REL)),
                      runToken: getTokenFromModeLink(runResource.links(LAST_RUN_REL)),
                      resource: runResource,
                    });
                  }
                }
              })
              .catch((err) =>
                this.store.dispatch({
                  type: NotebookActions.Type.RunFailed,
                  notebookToken: action.notebookToken,
                  error: err,
                })
              );
          } else if (resource && session) {
            this.store.dispatch({
              type: NotebookActions.Type.NoSession,
              notebookToken: action.notebookToken,
            });
          }

          return observableFrom([]);
        }),
        tap(() => this._hasClearedOutput$.next(false))
      )
      .subscribe();

    // Run new cells after notebook created
    this.actions$
      .pipe(
        ofType(NotebookActions.Type.DataLoaded),
        withLatestFrom(store.select(NotebookSelectors.notebookAwaitingRunArray)),
        filter(([action, awaitingRuns]) => awaitingRuns.get(action.notebookToken)),
        map(([action, awaitingRuns]) => action),
        withLatestFrom(
          store.select(NotebookSelectors.notebookCellTokensArray),
          store.select(NotebookSelectors.cellResourcesArray),
          store.select(NotebookSelectors.notebookSessionsArray),
          store.select(NotebookSelectors.cellLastRunTokensArray),
          store.select(NotebookSelectors.runsResultTokensArray),
          (action, cellTokens, resources, sessions, cellLastRunTokens, runsResultTokens) => ({
            action,
            cellResources: cellTokens
              .get(action.notebookToken)
              .map((t) => resources.get(t))
              .toArray(),
            session: sessions.get(action.notebookToken),
            cellLastRunTokens,
            runsResultTokens,
          })
        ),
        filter(
          ({ action, cellResources, session, cellLastRunTokens, runsResultTokens }) =>
            cellResources.length > 0 && session != null
        ),
        switchMap(({ action, cellResources, session, cellLastRunTokens, runsResultTokens }) => {
          if (cellResources.length > 0) {
            const cell = cellResources.find((res) => res.content_type !== NotebookTypes.ContentType.Markdown);
            if (cell) {
              return this.api
                .create<NotebookCellRunResource>(cell, NEW_RUN_REL, {
                  'python_cell.content': cell.content,
                  'python_cell.content_type': cell.content_type,
                  'python_cell.python_session.token': session.token,
                })
                .then((run) => {
                  const runToken = cellLastRunTokens.get(cell.token);

                  this.store.dispatch({
                    type: CellRunActions.Type.LoadLastCellRunSuccess,
                    cellToken: cell.token,
                    runToken,
                    resource: run,
                    resultTokens: runsResultTokens.get(runToken),
                  });

                  this.store.dispatch({
                    type: NotebookActions.Type.InitialRunSuccess,
                    notebookToken: action.notebookToken,
                  });
                })
                .catch((err) =>
                  this.store.dispatch({
                    type: CellActions.Type.RunFailed,
                    cellToken: cell.token,
                    error: err,
                    notebookToken: action.notebookToken,
                  })
                );
            }
          }
          return observableFrom([]);
        })
      )
      .subscribe();

    // Pusher side-effects
    // Filter only channels for notebooks
    this.listenToNotebookEvents();

    // Log notebook sidebar expanding/collapsing to analytics
    this.actions$
      .pipe(
        ofType(actions.EditorActionType.ToggleNotebookSidebar),
        withLatestFrom(
          store.select(NotebookSelectors.selectedNotebookToken),
          store.select(NotebookSelectors.selectedNotebookType),
          store.select(accountPreferenceSelector)
        )
      )
      .subscribe(
        ([
          action,
          notebook_token,
          notebook_kernel,
          {
            notebook_sidebar: { open, tab },
          },
        ]) => {
          const event = open
            ? AnalyticsEvent.Name.NotebookSidebarExpanded
            : AnalyticsEvent.Name.NotebookSidebarCollapsed;
          this.analytics.run(RunMethodType.Track, event, {
            product_context: AnalyticsEvent.Context.Notebook,
            product_subcontext: AnalyticsEvent.SubContext.Sidebar,
            interaction_type: 'click',
            notebook_token,
            notebook_kernel,
            tab,
          });
        }
      );
  }

  appendEventsToEventBus(dataStream: Observable<any>, channelName: string) {
    // Maintaing a map of the datastreams that have been subscribed to; to avoid duplication.
    if (this.eventBusMap.get(channelName)) {
      return;
    }

    dataStream.subscribe((event) => {
      this.eventBus$.next(event);
    });
    this.eventBusMap.set(channelName, dataStream);
  }

  listenToNotebookEvents() {
    this.eventBus$
      .pipe(
        withLatestFrom(
          this.store
            .select('client', 'notebooks', 'books', 'pusherChannelNames')
            .pipe(map((channels) => channels.toSet()))
        ),
        filter(([e, channels]) => channels.has(e.channel))
      )
      .subscribe(([e, channels]) => {
        const notebookToken = e.channel.split('-').pop();
        switch (e.name) {
          // confusing, notebookidle => cellidle, while batchruncomplete => notebookidle
          case NotebookPusherEvents.NotebookIdle:
            this.handleCellIdleEvent(e.data, notebookToken);
            break;
          case NotebookPusherEvents.BatchRunComplete:
            this.cleanUpRunsWithNoResults(notebookToken);
            this.store.dispatch({
              notebookToken,
              type: NotebookActions.Type.Idle,
            });
            break;
          case NotebookPusherEvents.DataLoaded:
            this.handleNotebookDataLoaded(e.data, notebookToken);
            break;
          case NotebookPusherEvents.ResultCreated:
            this.handleCellRunResultEvent(e.data, notebookToken);
            break;
          case NotebookPusherEvents.SessionTerminated:
            this.handleErrorState(e.data, notebookToken);
            break;
          case NotebookPusherEvents.SessionBusy:
            this.handleSessionBusyState(e.data, notebookToken);
            break;
          case NotebookPusherEvents.SessionMetrics:
            this.handleSessionMetrics(e.data, notebookToken);
            break;
          case NotebookPusherEvents.ExportDataframeCompleted:
            this.handleExportDataframeCompleted(e.data);
            break;
          case SocketEvents.Disconnected: // currently will never be received
            this.handleDisconnectState(e.data);
            break;
        }
      });
  }

  private updateCell(cell: NotebookCellResource, params: Partial<NotebookCellResource>) {
    const editParams = {
      'python_cell.content': params.content || cell.content,
      'python_cell.content_type': params.content_type || cell.content_type,
      'python_cell.frozen_in_layout': params.frozen_in_layout != null ? params.frozen_in_layout : cell.frozen_in_layout,
      includes: NEW_RUN_REL,
    };

    if (params.next_cell_token) {
      editParams['python_cell.next_cell_token'] = params.next_cell_token;
    }

    return cell.forms('edit').$submit(editParams);
  }

  /**
   * Load the notebook attached to a report (and all dependencies, like cells, runs, etc)
   * @param reportResource the resource object for the loaded report
   */
  private loadNotebook(reportResource: ReportResource): PromiseLike<any> {
    if (reportResource.hasLink(NOTEBOOK_REL)) {
      const notebookToken = getTokenFromModeLink(reportResource.links(NOTEBOOK_REL));
      return this.api
        .getResource<NotebookResource>(reportResource, NOTEBOOK_REL)
        .then((notebookResource) =>
          this.store.dispatch({
            type: NotebookActions.Type.LoadSuccess,
            notebookResource,
          })
        )
        .catch((error) =>
          this.store.dispatch({
            type: NotebookActions.Type.LoadFailure,
            notebookToken,
            error,
          })
        );
    }

    return this.$q.resolve();
  }

  private handleCellRunResultEvent(response, notebookToken: string) {
    const resource = new this.ModeResource(response);
    const runToken = getTokenFromModeLink(resource.links('python_cell_run'));
    const cellToken = getTokenFromModeLink(resource.links('python_cell'));

    this._hasClearedOutput$.next(false);

    // Add the cell token immediately to the state to retain sort order
    this.store.dispatch({
      type: CellRunActions.Type.AddCellToken,
      token: response.token,
    });

    this.cancelOrExecute(runToken, notebookToken, () =>
      this.api.getSelf(resource, { includes: 'python_cell_run' }).then((cellRunResult) =>
        this.store.dispatch({
          type: CellRunActions.Type.LoadCellRunResultSuccess,
          runToken: runToken,
          cellToken: cellToken,
          resultResource: cellRunResult,
        })
      )
    );
  }

  // handles the pusher notification that is sent after a cell export
  // this will be dispatched after a single cell export or after exported cell runs
  private handleExportDataframeCompleted(data: ICellExportPusherEventPayload) {
    this.store.dispatch({
      type: CellActions.Type.ExportDataframeCompleted,
      payload: data,
    });
  }

  private cleanUpRunsWithNoResults(notebookToken: string) {
    // This is mostly for empty cells that we track for cancellation but never end up
    //  having data returned from Pusher.
    this.cancellableRunTokens.forEach((runWithoutResult: string) =>
      this.cancelOrExecute(runWithoutResult, notebookToken, () => {})
    );
  }

  private cancelOrExecute(runToken: string, notebookToken: string, fn: () => void) {
    this.cancellableRunTokens.delete(runToken);
    if (this.isCancelling) {
      if (this.cancellableRunTokens.size === 0) {
        this.store.dispatch({
          type: NotebookActions.Type.CancelRunSuccess,
          notebookToken,
        });
        this.$timeout(
          () =>
            this.store.dispatch({
              type: NotebookActions.Type.CancelRunComplete,
              notebookToken,
            }),
          3000
        );
        this.cancellableRunTokens = new Set();
        this.isCancelling = false;
      }
    } else {
      fn();
    }
  }

  private handleCellIdleEvent(response, notebookToken: string) {
    const resource = new this.ModeResource(response);
    const cellToken = this.cellModel.getIdFromLink(resource.links('python_cell').href);

    // calls cancelOrExecute to handle cancelling a run after an idle event if no result was returned from Pusher
    this.cancelOrExecute(resource.token, notebookToken, () => {});

    this.store.dispatch({
      type: CellActions.Type.Idle,
      runToken: resource.token,
      cellToken,
    });
  }

  private handleNotebookDataLoaded(response, notebookToken) {
    this.store.dispatch({
      type: NotebookActions.Type.DataLoaded,
      notebookToken,
    });
  }

  private handleErrorState(response, notebookToken: string) {
    const resource = new this.ModeResource(response);
    const reason = response.termination_reason;

    this.store.dispatch({
      type: NotebookActions.Type.Terminated,
      terminationReason: reason,
      sessionToken: resource.token,
      notebookToken,
    });
  }

  private handleSessionBusyState(data, notebookToken: string) {
    this.store.dispatch({
      type: NotebookActions.Type.SessionBusy,
      notebookToken,
    });
  }

  private handleSessionMetrics(data, notebookToken: string) {
    this.store.dispatch({
      type: NotebookActions.Type.SessionMetrics,
      notebookToken,
      metrics: data.metrics,
    });
  }

  private handleDisconnectState(data) {
    this.store.dispatch({
      type: NotebookActions.Type.Disconnected,
    });
  }

  public loadNotebookInSelectedReport() {
    this.store.dispatch({
      type: NotebookActions.Type.LoadFromSelectedReport,
    });
  }

  /**
   * When the report run loads, load python notebook, polling for it to finish running if
   * necessary.
   */
  public pollPython() {
    this.store.dispatch({ type: CellActions.Type.LoadLayoutVisuals });

    return {
      success: this.actions$.pipe(
        ofType(CellActions.Type.LoadLayoutVisualsSuccess),
        map((action) => action.reportRun)
      ),
      failure: this.actions$.pipe(
        ofType(CellActions.Type.LoadLayoutVisualsFailure),
        map((action) => action.error)
      ),
    };
  }

  public initSession(notebookToken: string, reuse: boolean) {
    this.store.dispatch({
      type: NotebookActions.Type.InitSession,
      notebookToken: notebookToken,
      reuseIfActive: reuse,
    });
  }

  public refreshSessionReportRun(notebookToken: string) {
    this.store.dispatch({
      notebookToken,
      type: NotebookActions.Type.RefreshSessionReportRun,
    });
  }

  public initNotebook() {
    this.store.dispatch({
      type: NotebookActions.Type.Init,
    });
  }

  public cancelRun(notebookToken: string) {
    this.store.dispatch({
      type: NotebookActions.Type.CancelRun,
      notebookToken,
    });
  }

  // Shortcut function to support existing workflow with UI Router
  public loadNotebookWithInitSuccess(notebookResource: NotebookResource) {
    this.store.dispatch({
      type: NotebookActions.Type.LoadWithInitSuccess,
      notebookResource: notebookResource,
    });
  }

  public updateNotebookType(notebookToken: string, notebookType: NotebookTypes.NotebookType) {
    this.store.dispatch({
      type: NotebookActions.Type.UpdateType,
      notebookToken,
      notebookType,
    });
  }

  public addCell(notebookToken: string, nextCellToken?: string) {
    this.store.dispatch({
      type: CellActions.Type.AddCell,
      notebookToken,
      nextToken: nextCellToken,
    });
  }

  public addCellWithContent(notebookToken: string, content: string, contentType: NotebookTypes.ContentType) {
    this.store.dispatch({
      type: CellActions.Type.AddCell,
      notebookToken,
      content,
      contentType,
    });
  }

  public addCellAndRun(notebookToken: string, content: string) {
    this.store.dispatch({
      notebookToken,
      content,
      type: CellActions.Type.AddCellAndRun,
    });
  }

  public editCellContent(cellToken: string, content: string) {
    this.store.dispatch({
      cellToken,
      content,
      type: CellActions.Type.EditCellContent,
    });
  }

  public saveCell(cellToken: string) {
    this.store.dispatch({
      cellToken,
      type: CellActions.Type.UpdateCell,
    });
  }

  public updateContentType(cellToken: string, contentType: NotebookTypes.ContentType) {
    this.store.dispatch({
      cellToken,
      contentType,
      type: CellActions.Type.UpdateContentType,
    });
  }

  public removeCell(notebookToken: string, cellToken: string) {
    combineLatest([
      this.isNotebookExportDatasetsEnabled$,
      this.store.selectWithParam(NotebookSelectors.isExportedByCell, of(cellToken)),
      this.store.select(NotebookSelectors.createCellExecutionCountSelector(cellToken)),
      this.store.selectWithParam(NotebookSelectors.isPublishedDatasetCell, of(cellToken)),
      this.isNotebookDatasetsPublishedEnabled$,
    ])
      .pipe(first())
      .subscribe(([exportEnabled, isExported, executionCount, isPublishedDataset, publishedEnabled]) => {
        const getDialogText = () => {
          if (exportEnabled) {
            if (isExported) {
              return `
              <p>Deleting <b class="bold-text">cell ${executionCount}</b> is irreversible and will permanently break content in this Report${
                publishedEnabled && isPublishedDataset ? ` and the data workspace` : ``
              }.</p>
                  <p class='bold-text unset-margin'>The following will break:</p>
                  <ul class='unset-margin'>
                    <li>the local dataset created from this output cell</li>
                    <li>any calculated fields that were added to the dataset</li>
                    <li>any associated charts</li>
                    <li>any dependent Explorations</li>
                    ${
                      publishedEnabled && isPublishedDataset
                        ? ` <li>the published table imported in the data workspace</li>
                    <li>any dependent Models or Worksheets</li>
                    <li>any dependent Reports or Liveboards</li>
                    <li>any dependent Answers</li>`
                        : ``
                    }
                  </ul>`;
            }
            return `<p>Deleting <b class="bold-text">cell ${executionCount}</b> may impact other cells in the Notebook, and can’t be undone.</p>`;
          }
          return 'This may impact other cells in the Notebook and cannot be undone.';
        };

        this.confirmDialogService.open({
          dialogTitle: exportEnabled ? `Delete cell?` : `Delete cell ${executionCount}?`,
          dialogText: getDialogText(),
          confirmText: 'Delete',
          acknowledgeText:
            isPublishedDataset && publishedEnabled
              ? `I understand this will break content in this Report and the data workspace`
              : '',
          confirmAction: () => {
            this.store.dispatchThunk(
              NotebookSelectors.notebookCellTokensArray,
              NotebookSelectors.selectedNextCellToken,
              (notebookCellTokensArray, nextCellToken) => ({
                type: CellActions.Type.DestroyCell,
                cellToken,
                notebookToken,
                nextCellToken,
                notebookCellTokensArray,
              })
            );
          },
          confirmButtonColor: 'alert',
          confirmButtonType: 'primary',
        });
      });
  }

  public moveCell(cellToken: string, down: boolean) {
    this.store.dispatchThunk(NotebookSelectors.selectedNotebookToken, (notebookToken) => ({
      type: CellActions.Type.MoveCell,
      cellToken,
      notebookToken,
      down,
    }));
  }

  public addCellToLayout(cellToken: string) {
    this.store
      .select(NotebookSelectors.createIsCellInLayoutSelector(cellToken))
      .pipe(first((isInLayout) => isInLayout === false))
      .subscribe(() =>
        this.store.dispatch({
          type: CellActions.Type.AddToLayout,
          cellToken,
        })
      );
  }

  public removeCellFromLayout(cellToken: string) {
    this.store.dispatchThunk(NotebookSelectors.vizTokensArray, (vizTokensArray) => ({
      type: CellActions.Type.RemoveFromLayout,
      cellToken,
      vizToken: vizTokensArray.get(cellToken),
    }));
  }

  public selectCell(cellToken: string, resultPane: boolean) {
    this.store.dispatch({
      type: CellActions.Type.Select,
      cellToken,
      resultPane,
    });
  }

  public beginEditCell(cellToken: string) {
    this.store.dispatchThunk(NotebookSelectors.selectedCellFrozen, (cellFrozen) => ({
      type: CellActions.Type.BeginEdit,
      cellToken,
      cellFrozen,
    }));
  }

  public endEditCell(cellToken: string) {
    this.store.dispatch({
      type: CellActions.Type.EndEdit,
      cellToken,
    });
  }

  /**
   * Dispatches action to run all or some cells of a notebook. Markdown cells
   * will be excluded.
   *
   * @param notebookToken
   * @param cellTokensToRun cell tokens to run, or all cells if not provided
   */
  public runNotebook(notebookToken: string, cellTokensToRun?: string[]) {
    this.store.dispatchThunkPipe(
      NotebookSelectors.createRunnableCellTokensSelector(notebookToken),
      (obs) =>
        obs.pipe(
          map((tokens) => {
            if (cellTokensToRun != null) {
              return cellTokensToRun.filter((token) => tokens.has(token));
            }

            return tokens.toArray();
          })
        ),
      (tokens) => ({
        type: NotebookActions.Type.Run,
        notebookToken: notebookToken,
        runAllCells: cellTokensToRun == null,
        cellTokensToRun: tokens,
      })
    );
  }

  /**
   * Dispatches the action to run a single cell.
   * If it is a markdown cell, then the update action will be dispatched instead.
   * @param notebookToken
   * @param cellToken
   */
  public runCell(notebookToken: string, cellToken: string) {
    this.store.dispatchThunk(NotebookSelectors.createCellContentTypeSelector(cellToken), (contentType) => {
      if (contentType === NotebookTypes.ContentType.Python) {
        return {
          type: NotebookActions.Type.Run,
          notebookToken: notebookToken,
          runAllCells: false,
          cellTokensToRun: [cellToken],
        };
      } else {
        return {
          cellToken,
          type: CellActions.Type.UpdateCell,
        };
      }
    });
  }

  public toggleFreezeCell(cellToken: string) {
    this.store.dispatch({
      type: CellActions.Type.ToggleFreezeCell,
      cellToken,
    });
  }

  public toggleFoldCell(cellToken: string) {
    this.store.dispatch({
      type: CellActions.Type.FoldCell,
      cellToken,
    });
  }

  /**
   * Dispatchs the action to clear all cell outputs for the selected notebook
   * @param notebookToken
   */
  public clearAllCellOutputs(notebookToken: string) {
    this.store.dispatch({
      type: NotebookActions.Type.ClearAllCellOutputs,
      notebookToken,
    });
  }

  public get reportNotebookViz$() {
    return this.store.select(NotebookSelectors.reportVisuals);
  }

  // Not technically related to notebook's data
  // TODO: remove once ReportEditController can subscribe to rx state store
  public get sidebarOpen$() {
    return this.store.select((state) => state.transient.accountPreference.notebook_sidebar.open);
  }

  // For the report browser sidebar
  public get selectedNotebookType$() {
    return this.store.select(NotebookSelectors.selectedNotebookType);
  }
}
